import React, { useLayoutEffect, useState, useContext } from 'react';

import * as S from './styles';
import { color } from 'styles/pallete';
import { DataFormProps } from 'types/InputsProductCarouselProps';
import SimpleInput from 'components/SimpleInput';
import SimpleTextArea from 'components/SimpleTextArea';
import Check from 'components/Buttons/Check';
import { formatDateToISO, formatToDateCustom } from 'utils/functions';
import useDebounce from 'hooks/useDebounce';
import DynamicPageContext from 'context/DynamicPage';
import LoadGeneralContext from 'context/LoadGeneralContext';
import { getProductByCollectionOrQuery } from './reqs';
import SystemNotifyContext from 'context/SystemNotifyContext';
import { Product } from 'types/Product';

interface DataProductsCarouselProps {
	dataForm: DataFormProps;
	setDataForm: React.Dispatch<React.SetStateAction<DataFormProps>>;
	isShow: boolean;
	isShowDateInputs: boolean;
	setIsShowDateInputs: React.Dispatch<React.SetStateAction<boolean>>;
	setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
}

export default function DataProductsCarousel({
	isShow,
	dataForm,
	setDataForm,
	isShowDateInputs,
	setIsShowDateInputs,
	setProducts
}: DataProductsCarouselProps) {
	const { moduleInSavingMode, moduleOnPhoneToEdit } =
		useContext(DynamicPageContext);
	const { setMessage, setIsOpenLoad } = useContext(LoadGeneralContext);
	const { handleNotify } = useContext(SystemNotifyContext);

	const [editMode, setEditMode] = useState(false);

	const referenceIdToSearch = useDebounce(dataForm.reference_id, 1000);

	async function searchByProducts(valueToSearch: string) {
		setIsOpenLoad(true);
		setMessage('Buscando produtos');

		const { isSuccess, products, qtdAllProducts } =
			await getProductByCollectionOrQuery(valueToSearch, handleNotify);

		if (isSuccess) {
			setDataForm((obj) => ({
				...obj,
				qtdAllProducts: qtdAllProducts
			}));
			setProducts(products);
		} else {
			setDataForm((obj) => ({
				...obj,
				qtdAllProducts: 0
			}));
			setProducts([]);
		}

		setIsOpenLoad(false);
	}

	//busca o produto pelo sku com debounce
	useLayoutEffect(() => {
		(async () => {
			if (
				!editMode &&
				typeof referenceIdToSearch === 'string' &&
				referenceIdToSearch !== ''
			) {
				setDataForm((obj) => ({ ...obj, products: [] }));

				await searchByProducts(referenceIdToSearch);
			}

			if (referenceIdToSearch === '') {
				setDataForm((obj) => ({ ...obj, products: [] }));
			}
		})();
	}, [referenceIdToSearch]);

	//se estiver em edição aqui pega os dados do item editado
	useLayoutEffect(() => {
		if (moduleOnPhoneToEdit !== null && moduleInSavingMode === null) {
			if (moduleOnPhoneToEdit?.data?.products) {
				(async () => {
					await searchByProducts(moduleOnPhoneToEdit?.data?.reference_id || '');
					setEditMode(true);
				})();
			}
		}
	}, []);

	useLayoutEffect(() => {
		if (!isShowDateInputs) {
			setDataForm((obj) => ({
				...obj,
				countdown: false,
				schedule_end: '',
				schedule_start: ''
			}));
		}
	}, [isShowDateInputs]);

	useLayoutEffect(() => {
		if (dataForm.schedule_start !== '' || dataForm.schedule_end !== '') {
			setTimeout(() => {
				setIsShowDateInputs(true);
			}, 200);
		}
	}, []);

	return (
		<S.container $isShow={isShow}>
			<SimpleInput
				label="Título *"
				initialValue={dataForm.name}
				getValue={(value: string) => {
					setDataForm((obj) => ({ ...obj, name: value }));
				}}
			/>

			<S.qtdProdutos>
				<SimpleInput
					label="Query da lista*"
					initialValue={dataForm.reference_id}
					getValue={(value: string) => {
						setEditMode(false);
						setDataForm((obj) => ({ ...obj, reference_id: value }));
					}}
				/>
				<p>{`Quantidade de produtos: ${dataForm.qtdAllProducts}`}</p>
			</S.qtdProdutos>

			<SimpleTextArea
				colorFont={color.interface.lightGray}
				labelColor={color.interface.lightGray}
				borderColor={color.interface.lightGray2}
				textArea={dataForm.description}
				getValue={(value: string) => {
					setDataForm((obj) => ({
						...obj,
						description: value
					}));
				}}
				title={'Descrição'}
				rows={6}
			/>

			<div style={{ display: 'none' }}>
				<Check
					text="Programação"
					isChecked={isShowDateInputs}
					setIsChecked={() => {
						setIsShowDateInputs((obj) => !obj);
					}}
				/>

				{isShowDateInputs && (
					<>
						<S.twoInputs>
							<SimpleInput
								typeInput="datetime-local"
								label="Ínicio"
								initialValue={
									dataForm.schedule_start === ''
										? ''
										: formatToDateCustom(dataForm.schedule_start)
								}
								getValue={(value: string) => {
									setDataForm((obj) => {
										return {
											...obj,
											schedule_start: formatDateToISO(value)
										};
									});
								}}
								paddingInLineEnd
							/>

							<SimpleInput
								typeInput="datetime-local"
								label="Fim"
								initialValue={
									dataForm.schedule_end === ''
										? ''
										: formatToDateCustom(dataForm.schedule_end)
								}
								getValue={(value: string) => {
									setDataForm((obj) => {
										return {
											...obj,
											schedule_end: formatDateToISO(value)
										};
									});
								}}
								paddingInLineEnd
							/>
						</S.twoInputs>

						<Check
							text="Contagem regressiva"
							isChecked={dataForm.countdown}
							setIsChecked={() => {
								setDataForm((obj) => {
									return {
										...obj,
										countdown: !obj.countdown
									};
								});
							}}
						/>
					</>
				)}
			</div>
		</S.container>
	);
}
